import { inject, Injectable } from '@angular/core';
import { GeneralActions } from '@hunting-nature/Secacam/shared/redux/general/general.actions';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, from, map, of, switchMap, tap } from 'rxjs';

import { ContactsRestService } from '../../../shared/services/contacts/contacts.rest.service';
import { ToastService } from '../../../shared/services/toast.service';

import { MenuContactsActions } from './menu-contacts.actions';

import { IonNavService } from '@hunting-nature/Secacam/shared/services/ion-nav.service';

@Injectable()
export class MenuContactsEffects {
  private readonly actions$ = inject(Actions);
  private readonly contactsRestService = inject(ContactsRestService);
  private readonly ionNavService = inject(IonNavService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);

  readonly contactsOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuContactsActions.contactsOpened, MenuContactsActions.goBackToContacts),
      switchMap(() =>
        this.contactsRestService.fetchAllContacts$().pipe(
          map(contacts => MenuContactsActions.contactsLoadSucceeded({ contacts })),
          catchError((error: unknown) => of(MenuContactsActions.contactsLoadFailed({ error })))
        )
      )
    )
  );

  readonly updateUsername$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuContactsActions.updateUsername),
      switchMap(({ contactId, username, originalUsername }) =>
        this.contactsRestService.updateUsername$(contactId, username).pipe(
          map(() => MenuContactsActions.updateUsernameSucceeded({ contactId })),
          catchError((error: unknown) => of(MenuContactsActions.updateUsernameFailed({ contactId, originalUsername, error })))
        )
      )
    )
  );

  readonly updateUsernameFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuContactsActions.updateUsernameFailed),
        tap(() => {
          const message = this.transloco.translate('general.contactsMenu.updateNameFailed');
          void this.toastService.showErrorMessage$(message);
        })
      ),
    { dispatch: false }
  );

  readonly viewUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuContactsActions.viewUserProfile),
      map(() => GeneralActions.openComingSoonModal())
    )
  );

  readonly goToInviteNewContact$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuContactsActions.goToInviteNewContact),
        switchMap(async () => {
          const InviteNewContactPage = await import('../pages/invite-new-contact/invite-new-contact.page').then(m => m.InviteNewContactPage);
          return from(this.ionNavService.pushPage(InviteNewContactPage));
        })
      ),
    { dispatch: false }
  );

  readonly goBackToMenuFromInviteNewContact$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuContactsActions.goBackToMenuFromInviteNewContact, MenuContactsActions.goBackToContacts),
        switchMap(() => from(this.ionNavService.goBack()))
      ),
    { dispatch: false }
  );

  readonly inviteNewContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuContactsActions.inviteNewContact),
      switchMap(action =>
        this.contactsRestService
          .addContact$({
            alias: action.contactName,
            invitationEmail: action.contactEmail,
            sharedCameras: [],
            acceptDate: undefined,
          })
          .pipe(
            map(() => MenuContactsActions.inviteNewContactSucceeded()),
            catchError((error: unknown) => of(MenuContactsActions.inviteNewContactFailed({ error })))
          )
      )
    )
  );

  readonly inviteNewContactFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuContactsActions.inviteNewContactFailed),
        tap(() => {
          const message = this.transloco.translate('menu.contacts.newContact.result.error.message');
          void this.toastService.showErrorMessage$(message);
        })
      ),
    { dispatch: false }
  );

  readonly deleteContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuContactsActions.deleteContact),
      switchMap(({ contactId }) =>
        this.contactsRestService.deleteContact$(contactId).pipe(
          map(() => MenuContactsActions.deleteContactSucceeded({ contactId })),
          catchError((error: unknown) => of(MenuContactsActions.deleteContactFailed({ error })))
        )
      )
    )
  );

  readonly deleteContactFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuContactsActions.deleteContactFailed),
        tap(() => {
          const message = this.transloco.translate('general.contactsMenu.deleteContactFailed');
          void this.toastService.showErrorMessage$(message);
        })
      ),
    { dispatch: false }
  );

  readonly resendInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuContactsActions.resendInvitation),
      switchMap(({ contactId }) =>
        this.contactsRestService.resendInvitation$(contactId).pipe(
          map(() => MenuContactsActions.resendInvitationSucceeded()),
          catchError((error: unknown) => of(MenuContactsActions.resendInvitationFailed({ error })))
        )
      )
    )
  );

  readonly resendInvitationFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuContactsActions.resendInvitationFailed),
        tap(() => {
          const message = this.transloco.translate('general.contactsMenu.resendInviteFailed');
          void this.toastService.showErrorMessage$(message);
        })
      ),
    { dispatch: false }
  );
}
